import { useState, useEffect, useCallback, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Box, Package, PackageCheck, AlertTriangle, BarChart3, Truck, Check,
  RefreshCw, Archive, CircleAlert, MessageCircle
} from 'lucide-react';
import { api } from '../../api/client';
import { useUIStore } from '../../stores/uiStore';
import { useAuthStore, type UserPreferences } from '../../stores/authStore';
import { Can, useCan, visibleTabs, pickActiveTab, NoAccess } from '../../lib/rbac';
import { useSSEStore } from '../../stores/sseStore';

import { TerminalSelector } from './TerminalSelector';
import { InternalPendingBanner } from './InternalPendingBanner';
import { ReleasedHoldsBanner } from './ReleasedHoldsBanner';
import { BatchData, ScanResult } from './shared';

import { StatCard } from './components/StatCard';
import { ScanBar } from './components/ScanBar';

import { BagBanner } from './bags/BagBanner';
import { BagsList } from './bags/BagsList';
import { BagGateModal } from './bags/BagGateModal';
import { CourierChoiceModal } from './bags/CourierChoiceModal';
import type { WhCourier } from './shared';

import { BatchesTab } from './tabs/BatchesTab';
import { ReadyToShipTab } from './tabs/ReadyToShipTab';
import { AlertsTab } from './tabs/AlertsTab';
import { MetricsTab } from './tabs/MetricsTab';
import { HistoryTab } from './tabs/HistoryTab';
import { AllSellersTab } from './tabs/AllSellersTab';

import { ScanResultModal } from './modals/ScanResultModal';
import { ReturnScanModal } from './modals/ReturnScanModal';
import { AmazonMultiModal } from './modals/AmazonMultiModal';
import { LinkTrackingModal } from './modals/LinkTrackingModal';

type Tab = 'batches' | 'ready' | 'alerts' | 'metrics' | 'history' | 'all' | 'bags';

// Fecha local 'YYYY-MM-DD' — para la compuerta de bolsa (una vez por jornada).
function localDateStr(): string {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
}

export default function WarehousePage() {
  const { t } = useTranslation();
  const can = useCan();
  // Quien no opera el depósito mira: sin escaneo, sin bolsas, sin impresiones.
  const canOperate = can('warehouse.operate');
  const { sellerId } = useUIStore();

  const [tab, setTab] = useState<Tab>('batches');
  const [batches, setBatches] = useState<BatchData[]>([]);
  const [sellers, setSellers] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [scanResult, setScanResult] = useState<ScanResult | null>(null);
  // Paquete Amazon multi-producto: convive con scanResult (el popup clásico se
  // abre por encima al tocar una tarjeta) — ver AmazonMultiModal.
  const [multiResult, setMultiResult] = useState<ScanResult | null>(null);
  const [scanLoading, setScanLoading] = useState(false);
  const [guardProblems, setGuardProblems] = useState<any[]>([]);
  const [readyCount, setReadyCount] = useState(0);
  const scanInputRef = useRef<HTMLInputElement>(null);
  const [terminalId, setTerminalId] = useState<string | null>(() =>
    typeof localStorage !== 'undefined' ? localStorage.getItem('cbt_terminal_id') : null
  );
  const [linkModalCode, setLinkModalCode] = useState<string | null>(null);
  const [bagRefreshKey, setBagRefreshKey] = useState(0);
  // Preferencia PERSONAL "ver los mensajes del comprador en el pop-up de cierre". El interruptor
  // también está en Configuración › Depósitos, pero el operario de la mesa no entra ahí (no tiene
  // esa pantalla), así que lo tiene acá, a mano. Guarda en su usuario, no en el navegador.
  const whPopupMessages = useAuthStore((s) => s.user?.preferences?.wh_popup_messages === true);
  const setPreferences = useAuthStore((s) => s.setPreferences);
  const [savingPopupPref, setSavingPopupPref] = useState(false);
  const [popupPrefError, setPopupPrefError] = useState<string | null>(null);
  // One-shot flag: si está en true, el próximo /items o /add-and-close incluye
  // no_bag_reason='voluminoso' (item va en su propia caja, sin bolsa).
  const [noBagNextScan, setNoBagNextScan] = useState(false);
  // Compuerta de bolsa: al arrancar el batch hay que escanear/confirmar una
  // bolsa antes de poder cargar productos. activeBag se comparte con el banner.
  const [activeBag, setActiveBag] = useState<{ id: number; bag_code: string } | null>(null);
  const [activeBagLoaded, setActiveBagLoaded] = useState(false);
  const [bagGateConfirmedDate, setBagGateConfirmedDate] = useState<string | null>(() =>
    typeof localStorage !== 'undefined' ? localStorage.getItem('cbt_wh_bag_gate_date') : null
  );
  // Resalte temporal del ScanBar: tras cerrar/escanear bolsa la vista vuelve al
  // cuadro de escaneo y lo destaca ~1.6s para que el operador no lo busque.
  const [scanBarHighlight, setScanBarHighlight] = useState(false);

  const focusScanBar = useCallback(() => {
    setTimeout(() => {
      scanInputRef.current?.scrollIntoView({ behavior: 'smooth', block: 'center' });
      scanInputRef.current?.focus();
      setScanBarHighlight(true);
      setTimeout(() => setScanBarHighlight(false), 1600);
    }, 120);
  }, []);

  // 2026-09-10 — depósito con dos correos: si ya hay una bolsa abierta por correo, el backend
  // responde 409 NEED_COURIER_CHOICE y se pregunta "¿para qué correo es?" con botones grandes.
  const [courierChoice, setCourierChoice] = useState<{ code: string; options: WhCourier[] } | null>(null);
  const [courierChoiceBusy, setCourierChoiceBusy] = useState(false);

  const handleScanBag = useCallback(async (code: string, courierKey?: string | null) => {
    const res = await fetch('/api/warehouse/bags/scan', {
      method: 'POST',
      credentials: 'include',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ bag_code: code, ...(courierKey ? { courier_key: courierKey } : {}) }),
    });
    if (!res.ok) {
      const err = await res.json().catch(() => ({ error: 'Error al abrir bolsa' }));
      if (err?.code === 'NEED_COURIER_CHOICE' && Array.isArray(err?.meta?.options)) {
        setCourierChoice({ code, options: err.meta.options });
        return;
      }
      alert(err.error ?? 'Error al abrir bolsa');
      return;
    }
    setCourierChoice(null);
    setBagRefreshKey((k) => k + 1);
    focusScanBar();
  }, [focusScanBar]);

  const handleCourierChosen = useCallback(async (courierKey: string) => {
    if (!courierChoice) return;
    setCourierChoiceBusy(true);
    try {
      await handleScanBag(courierChoice.code, courierKey);
    } finally {
      setCourierChoiceBusy(false);
    }
  }, [courierChoice, handleScanBag]);

  const handleCloseBag = useCallback(async (bagId: number) => {
    const res = await fetch(`/api/warehouse/bags/${bagId}/close`, {
      method: 'POST',
      credentials: 'include',
    });
    if (!res.ok) {
      alert('Error al cerrar bolsa');
      return;
    }
    setBagRefreshKey((k) => k + 1);
  }, []);

  const loadActiveBag = useCallback(async () => {
    try {
      const res = await fetch('/api/warehouse/bags/active', { credentials: 'include' });
      const d = res.ok ? await res.json() : { bag: null };
      setActiveBag(d.bag ?? null);
    } catch {
      setActiveBag(null);
    } finally {
      setActiveBagLoaded(true);
    }
  }, []);

  const handleBagGateConfirmed = useCallback(() => {
    const today = localDateStr();
    localStorage.setItem('cbt_wh_bag_gate_date', today);
    setBagGateConfirmedDate(today);
    setBagRefreshKey((k) => k + 1); // refresca activeBag (compuerta) + BagBanner
    focusScanBar(); // la compuerta se cierra → volver al cuadro de escaneo
  }, [focusScanBar]);

  const runGuardCheck = useCallback(async () => {
    try {
      const res: any = await api.get(`/warehouse/guard-check?seller_id=${sellerId}`);
      if (res.success) setGuardProblems(res.problems || []);
    } catch (err) { console.error('Guard check failed', err); }
  }, [sellerId]);

  /**
   * Prende/apaga la preferencia personal del pop-up. El cliente `api` NO lanza en 4xx: se mira
   * `success`. Solo se refleja en la sesión lo que el servidor devolvió ya guardado.
   */
  async function togglePopupMessages() {
    if (savingPopupPref) return;
    setSavingPopupPref(true);
    setPopupPrefError(null);
    try {
      const res = (await api.put<unknown>('/auth/me/preferences', {
        wh_popup_messages: !whPopupMessages,
      })) as unknown as {
        success: boolean;
        error?: string;
        data?: { preferences?: UserPreferences };
      };
      if (res.success) setPreferences(res.data?.preferences ?? {});
      else setPopupPrefError(res.error || 'No se pudo guardar tu preferencia');
    } catch {
      setPopupPrefError('No se pudo guardar tu preferencia: sin conexión con el servidor');
    } finally {
      setSavingPopupPref(false);
    }
  }

  const loadBatches = useCallback(async () => {
    setLoading(true);
    try {
      const res: any = await api.get(`/warehouse/batches/active?seller_id=${sellerId}`);
      if (res.success) {
        setBatches(res.batches || []);
        setSellers(res.sellers || []);
      }
    } catch (err) { console.error('Failed to load batches', err); }
    finally { setLoading(false); }
  }, [sellerId]);

  const loadReadyCount = useCallback(async () => {
    try {
      const ids = sellers.map(s => s.id).join(',');
      const res: any = await api.get(`/warehouse/ready-to-ship?seller_id=${sellerId}&seller_ids=${ids}&limit=1`);
      if (res.success) setReadyCount(res.meta?.total || 0);
    } catch { /* ignore */ }
  }, [sellerId, sellers]);

  useEffect(() => { loadBatches(); runGuardCheck(); }, [loadBatches, runGuardCheck]);
  useEffect(() => { loadActiveBag(); }, [loadActiveBag, bagRefreshKey]);
  useEffect(() => { if (sellers.length > 0) loadReadyCount(); }, [sellers, loadReadyCount]);

  useEffect(() => {
    const batchTimer = setInterval(loadBatches, 60000);
    const guardTimer = setInterval(runGuardCheck, 300000);
    return () => { clearInterval(batchTimer); clearInterval(guardTimer); };
  }, [loadBatches, runGuardCheck]);

  // SSE real-time: refresh on warehouse events
  const { subscribe } = useSSEStore();
  const batchRef = useRef(loadBatches);
  const guardRef = useRef(runGuardCheck);
  batchRef.current = loadBatches;
  guardRef.current = runGuardCheck;
  useEffect(() => {
    const refreshBatches = () => batchRef.current();
    const refreshGuard = () => { guardRef.current(); batchRef.current(); };
    const unsubs = [
      subscribe('warehouse:guard_alert', refreshGuard),
      subscribe('warehouse:item_received', refreshBatches),
      subscribe('warehouse:item_packed', refreshBatches),
      subscribe('warehouse:item_closed', refreshBatches),
      subscribe('warehouse:batch_closed', refreshBatches),
      subscribe('warehouse:batch_reopened', refreshBatches),
      subscribe('warehouse:batch_released', refreshBatches),
    ];
    return () => unsubs.forEach(u => u());
  }, [subscribe]);

  const handleScan = async (code: string) => {
    if (!code.trim()) return;
    setScanLoading(true);
    try {
      const res: any = await api.post('/warehouse/scan', { code: code.trim(), seller_id: sellerId });
      if (res.success) {
        if (res.type === 'amazon_multi') {
          // Paquete con varios productos: lista completa, sin popup clásico
          setMultiResult(res);
          setScanResult(null);
        } else {
          setScanResult(res);
        }
      }
    } catch (err) { console.error('Scan error', err); }
    finally { setScanLoading(false); }
  };

  // Flujo 1×1 del paquete multi: el operario tocó una tarjeta → popup clásico
  // de ESA compra (el backend imprime su etiqueta ML como en un scan normal).
  const handleSelectMultiProduct = async (purchaseId: number) => {
    if (!multiResult?.code) return;
    setScanLoading(true);
    try {
      const res: any = await api.post('/warehouse/scan', {
        code: multiResult.code, purchase_id: purchaseId, seller_id: sellerId,
      });
      if (res.success) setScanResult(res);
    } catch (err) { console.error('Multi select error', err); }
    finally { setScanLoading(false); }
  };

  // Compute stats for active batches
  const totalReceived = batches.reduce((s, b) => s + b.counts.received, 0);
  const totalPacked = batches.reduce((s, b) => s + b.counts.packed, 0);
  const totalClosed = batches.reduce((s, b) => s + b.counts.closed, 0);

  // Compuerta de bolsa: bloquea hasta escanear/confirmar bolsa si no se confirmó
  // hoy, o si directamente no hay ninguna bolsa abierta.
  const bagGateOpen =
    activeBagLoaded && (bagGateConfirmedDate !== localDateStr() || !activeBag);

  const tabs: { key: Tab; label: string; icon: any; badge?: number }[] = [
    { key: 'batches', label: t('wh.batches'), icon: Package },
    { key: 'bags', label: 'Bolsas del batch', icon: Package },
    { key: 'ready', label: t('wh.readyToShip'), icon: Truck, badge: readyCount },
    { key: 'alerts', label: t('wh.alerts'), icon: AlertTriangle },
    { key: 'metrics', label: t('wh.metrics'), icon: BarChart3 },
    { key: 'history', label: t('wh.history'), icon: Archive },
    // La pestaña "Todos los Sellers" ya no depende del rol: la habilita warehouse.tab.all_sellers.
    { key: 'all', label: 'Todos los Sellers', icon: Box },
  ];

  const allowedTabs = visibleTabs('warehouse', tabs, can);
  const active = pickActiveTab('warehouse', tabs, tab, can) as Tab | null;
  useEffect(() => {
    if (active && active !== tab) setTab(active);
  }, [active, tab]);

  if (active === null) return <NoAccess />;

  if (!terminalId) {
    return <TerminalSelector onSelected={setTerminalId} />;
  }

  return (
    <div className="space-y-4">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <Box size={24} className="text-[var(--color-primary)]" />
          <h1 className="text-xl font-bold text-[var(--color-text)]">{t('wh.title')}</h1>
          <span className="text-xs px-2 py-0.5 rounded-full bg-[var(--color-bg-hover)] text-[var(--color-text-secondary)] font-mono">
            {terminalId}
          </span>
          <button
            onClick={() => {
              localStorage.removeItem('cbt_terminal_id');
              setTerminalId(null);
            }}
            className="text-xs px-2 py-0.5 rounded text-[var(--color-text-secondary)] hover:bg-[var(--color-bg-hover)] hover:text-[var(--color-primary)]"
            title="Cambiar terminal"
          >
            cambiar
          </button>
        </div>
        <div className="flex items-center gap-2">
          {/* Interruptor personal: ver el chat del comprador dentro del pop-up de cierre */}
          <Can cap="warehouse.operate">
            <button
              type="button"
              role="switch"
              aria-checked={whPopupMessages}
              disabled={savingPopupPref}
              onClick={togglePopupMessages}
              title={t('wh.popupMessagesHint')}
              className={`flex items-center gap-1.5 rounded-lg border px-2 py-1 text-xs font-medium transition-colors disabled:opacity-50 ${
                whPopupMessages
                  ? 'border-blue-400 bg-blue-50 text-blue-700 dark:border-blue-600 dark:bg-blue-900/30 dark:text-blue-300'
                  : 'border-[var(--color-border)] text-[var(--color-text-secondary)] hover:bg-[var(--color-bg-hover)]'
              }`}
            >
              <MessageCircle size={14} />
              <span className="hidden sm:inline">{t('wh.popupMessagesToggle')}</span>
              <span
                className={`inline-flex h-4 w-7 shrink-0 items-center rounded-full transition-colors ${
                  whPopupMessages ? 'bg-blue-500' : 'bg-gray-400'
                }`}
              >
                <span
                  className={`inline-block h-3 w-3 transform rounded-full bg-white shadow transition-transform ${
                    whPopupMessages ? 'translate-x-3.5' : 'translate-x-0.5'
                  }`}
                />
              </span>
            </button>
          </Can>
          {popupPrefError && (
            <span className="flex items-center gap-1 text-xs text-red-600 dark:text-red-400">
              <AlertTriangle size={12} className="shrink-0" />
              {popupPrefError}
            </span>
          )}
          <button onClick={loadBatches} className="p-2 rounded-lg hover:bg-[var(--color-bg-hover)] text-[var(--color-text-secondary)]">
            <RefreshCw size={18} className={loading ? 'animate-spin' : ''} />
          </button>
        </div>
      </div>

      {/* Internal stock pending banner — pickable orders */}
      <InternalPendingBanner sellerId={sellerId} onPick={(result) => setScanResult(result)} />
      <ReleasedHoldsBanner sellerId={sellerId} />

      {/* Scan Input Bar — always visible at top */}
      <Can cap="warehouse.operate">
        <ScanBar inputRef={scanInputRef} onScan={handleScan} loading={scanLoading} highlight={scanBarHighlight} />

        {/* One-shot: marcar el próximo scan como "sin bolsa" (voluminoso) */}
        <label className="flex items-center gap-2 text-sm cursor-pointer select-none px-1">
          <input
            type="checkbox"
            checked={noBagNextScan}
            onChange={(e) => setNoBagNextScan(e.target.checked)}
            className="accent-orange-600"
          />
          <span className={noBagNextScan ? 'text-orange-700 font-semibold' : 'text-[var(--color-text-secondary)]'}>
            📦 Próximo scan: <strong>sin bolsa</strong> (voluminoso — va en caja propia)
          </span>
        </label>
      </Can>

      {/* Stats Panel */}
      {batches.length > 0 && (
        <div className="grid grid-cols-2 sm:grid-cols-5 gap-3">
          <StatCard label={t('wh.batches')} value={batches.length} color="text-[var(--color-text)]" icon={Package} />
          <StatCard label={t('wh.received')} value={totalReceived} color="text-yellow-600" icon={Box} />
          <StatCard label={t('wh.packed')} value={totalPacked} color="text-blue-600" icon={PackageCheck} />
          <StatCard label={t('wh.closed')} value={totalClosed} color="text-green-600" icon={Check} />
          <StatCard label={t('wh.readyToShip')} value={readyCount} color="text-cyan-600" icon={Truck} />
        </div>
      )}

      {/* Amazon Multi Modal — paquete con varios productos. Va ANTES del popup
          clásico en el DOM para que éste (mismo z-index) pinte por encima al
          tocar una tarjeta. */}
      {multiResult && (
        <AmazonMultiModal
          result={multiResult}
          batches={batches}
          noBagReason={noBagNextScan ? 'voluminoso' : null}
          onSelectProduct={handleSelectMultiProduct}
          onClose={() => {
            setMultiResult(null);
            setNoBagNextScan(false);
            setTimeout(() => scanInputRef.current?.focus(), 100);
          }}
          onAllDone={() => {
            loadBatches();
            setMultiResult(null);
            setNoBagNextScan(false);
            setTimeout(() => scanInputRef.current?.focus(), 100);
          }}
        />
      )}

      {/* Devolución recibida (guía de devolución escaneada, 2026-09-23): pop-up propio,
          con el historial de la venta y las acciones de la cancelada. */}
      {scanResult && scanResult.type === 'ml_return' && (
        <ReturnScanModal
          result={scanResult}
          sellerId={sellerId}
          onClose={() => {
            setScanResult(null);
            setNoBagNextScan(false);
            setTimeout(() => scanInputRef.current?.focus(), 100);
          }}
        />
      )}

      {/* Scan Result Modal */}
      {scanResult && scanResult.type !== 'ml_return' && (
        <ScanResultModal
          result={scanResult}
          sellerId={sellerId}
          batches={batches}
          noBagReason={noBagNextScan ? 'voluminoso' : null}
          onClose={() => {
            setScanResult(null);
            setNoBagNextScan(false);
            setTimeout(() => scanInputRef.current?.focus(), 100);
          }}
          onItemAdded={() => {
            loadBatches();
            setScanResult(null);
            setNoBagNextScan(false); // one-shot: consumido tras éxito
            if (multiResult?.code) {
              // Venía de la lista del paquete multi: re-escanear el tracking
              // refresca la lista con el estado REAL de la base (el producto
              // recién cerrado aparece en verde), sin re-escaneo físico.
              handleScan(multiResult.code);
            } else {
              setTimeout(() => scanInputRef.current?.focus(), 100);
            }
          }}
          onRequestLinkTracking={(code) => { setScanResult(null); setLinkModalCode(code); }}
          onRescan={(code) => { setScanResult(null); handleScan(code); }}
        />
      )}

      {/* Bag Gate — bloquea hasta confirmar bolsa al arrancar el batch (solo a quien opera) */}
      {bagGateOpen && canOperate && (
        <BagGateModal activeBag={activeBag} onConfirmed={handleBagGateConfirmed} />
      )}
      {courierChoice && (
        <CourierChoiceModal
          bagCode={courierChoice.code}
          options={courierChoice.options}
          busy={courierChoiceBusy}
          onChoose={handleCourierChosen}
          onCancel={() => setCourierChoice(null)}
        />
      )}

      {/* Link Tracking Modal — fallback when scan has no match */}
      {linkModalCode && (
        <LinkTrackingModal
          tracking={linkModalCode}
          sellerId={sellerId}
          onClose={() => setLinkModalCode(null)}
          onLinked={async () => {
            const code = linkModalCode;
            setLinkModalCode(null);
            setScanResult(null);
            await handleScan(code);
          }}
        />
      )}

      {/* Guard Alerts Banner */}
      {guardProblems.length > 0 && (
        <div className="bg-red-50 dark:bg-red-900/20 border-2 border-red-400 dark:border-red-600 rounded-lg p-4 animate-pulse">
          <div className="flex items-center gap-3">
            <CircleAlert size={24} className="text-red-600 shrink-0" />
            <div className="flex-1">
              <p className="text-sm font-bold text-red-800 dark:text-red-200">
                {guardProblems.length} {guardProblems.length === 1 ? 'orden con problema' : 'ordenes con problemas'} en lotes activos
              </p>
              <div className="mt-1 space-y-1">
                {guardProblems.slice(0, 5).map((p: any, i: number) => (
                  <div key={i} className="flex items-center gap-2">
                    <p className="text-xs text-red-700 dark:text-red-300 flex-1">
                      {p.problem_type === 'order_cancelled' ? '--- CANCELADA' :
                       p.problem_type === 'shipment_cancelled' ? '--- ENVIO CANCELADO' : '!!! RECLAMO'}{' '}
                      {p.bag_code && <span className="font-bold">[BOLSA {p.bag_code}] </span>}
                      -- Orden #{p.ml_order_id} -- Batch #{p.batch_id}
                      {p.ml_shipment_id && ` -- Envío #${p.ml_shipment_id}`}
                      {p.buyer_nickname && ` -- ${p.buyer_nickname}`}
                      {p.search_label_printed_at && <span className="text-green-700"> ✓ etiqueta impresa</span>}
                    </p>
                    {p.problem_type !== 'claim_opened' && canOperate && (
                      <button
                        onClick={async () => {
                          try {
                            await api.post(`/warehouse/search-label/${p.wh_item_id}`, {});
                            runGuardCheck();
                          } catch (e: any) { alert(e?.message || 'Error al imprimir'); }
                        }}
                        className="text-xs px-2 py-0.5 rounded bg-red-600 text-white hover:bg-red-700 shrink-0"
                        title="Imprimir etiqueta de búsqueda (bolsa + datos + foto)"
                      >🖨 Etiqueta</button>
                    )}
                  </div>
                ))}
                <Can cap="warehouse.operate">
                  <button
                    onClick={async () => {
                      try {
                        const r: any = await api.post('/warehouse/search-labels/all', {});
                        alert(r?.count ? `${r.count} etiquetas de búsqueda enviadas a imprimir (ordenadas por bolsa)` : 'No hay canceladas pendientes');
                        runGuardCheck();
                      } catch (e: any) { alert(e?.message || 'Error al imprimir'); }
                    }}
                    className="mt-2 text-xs px-3 py-1 rounded bg-red-700 text-white font-bold hover:bg-red-800"
                  >🖨 Imprimir TODAS las etiquetas de búsqueda</button>
                </Can>
                {guardProblems.length > 5 && <p className="text-xs text-red-600 dark:text-red-400">... y {guardProblems.length - 5} mas</p>}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Banner bolsa global — siempre visible en todas las tabs */}
      <BagBanner onScan={handleScanBag} onClose={handleCloseBag} refreshKey={bagRefreshKey} />

      {/* Tabs */}
      <div className="flex gap-1 border-b border-[var(--color-border)]">
        {allowedTabs.map(({ key, label, icon: Icon, badge }) => (
          <button key={key} onClick={() => setTab(key)}
            className={`flex items-center gap-2 px-4 py-2.5 text-sm font-medium border-b-2 transition-colors ${
              active === key ? 'border-[var(--color-primary)] text-[var(--color-primary)]'
                : 'border-transparent text-[var(--color-text-secondary)] hover:text-[var(--color-text)]'
            }`}
          >
            <Icon size={16} />{label}
            {badge != null && badge > 0 && (
              <span className="px-1.5 py-0.5 rounded-full text-[10px] font-bold bg-cyan-100 text-cyan-700 dark:bg-cyan-900/30 dark:text-cyan-300">{badge}</span>
            )}
          </button>
        ))}
      </div>

      {active === 'batches' && <BatchesTab batches={batches} sellerId={sellerId} onRefresh={loadBatches} guardProblems={guardProblems} />}
      {active === 'bags' && <BagsList refreshKey={bagRefreshKey} />}
      {active === 'ready' && <ReadyToShipTab sellerId={sellerId} sellers={sellers} />}
      {active === 'alerts' && <AlertsTab sellerId={sellerId} />}
      {active === 'metrics' && <MetricsTab sellerId={sellerId} />}
      {active === 'history' && <HistoryTab sellerId={sellerId} />}
      {active === 'all' && <AllSellersTab />}
    </div>
  );
}
